import React, { useState, useEffect } from 'react';
// Removed framer-motion animations
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, addDoc, updateDoc, doc } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { createCheckoutSession } from '../lib/payments';
import { auth } from '../firebase';
import { doc as docRef, setDoc } from 'firebase/firestore';
import { useEntitlement } from '../hooks/useEntitlement';
import TrialBanner from './TrialBanner';

interface MembershipTier {
  id: string;
  name: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  description: string;
  features: string[];
  tagline?: string;
  isPopular?: boolean;
  isActive: boolean;
  maxMembers?: number;
  currentMembers: number;
  discount?: number; // percentage discount for yearly
  color: string;
  icon: string;
  isCustomPricing?: boolean; // For custom pricing tiers like Pinnacle
  order?: number; // Order for display
}

interface MembershipSubscription {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  tierId: string;
  tierName: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  status: 'active' | 'cancelled' | 'expired' | 'pending';
  startDate: Date;
  endDate: Date;
  nextBillingDate: Date;
  paymentMethod?: string;
  autoRenew: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface PaymentMethod {
  id: string;
  userId: string;
  type: 'card' | 'bank' | 'paypal';
  last4?: string;
  brand?: string;
  isDefault: boolean;
  expiryDate?: string;
}

const MembershipPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const { entitlement } = useEntitlement();
  const [activeTab, setActiveTab] = useState<'plans' | 'current' | 'billing' | 'benefits'>('plans');
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [userSubscription, setUserSubscription] = useState<MembershipSubscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedTier, setSelectedTier] = useState<MembershipTier | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Checkout form states
  const [checkoutForm, setCheckoutForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Netherlands',
    acceptTerms: false,
    acceptMarketing: false
  });

  // Payment form states
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    saveCard: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load membership tiers with fallback when composite index is missing
      let tiersData: MembershipTier[] = [];
      try {
        const tiersQuery = query(
          collection(db, 'membershipTiers'),
          where('isActive', '==', true),
          orderBy('price', 'asc')
        );
        const tiersSnapshot = await getDocs(tiersQuery);
        tiersData = tiersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as MembershipTier[];
      } catch (err) {
        // Fallback: no composite index yet → fetch without orderBy and sort client-side
        const fallbackQuery = query(
          collection(db, 'membershipTiers'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(fallbackQuery);
        tiersData = (snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as MembershipTier[]).sort((a, b) => (a.price || 0) - (b.price || 0));
      }
      // Normalize fields for robustness (support benefits/priceYear fallbacks)
      const normalizedTiers = (tiersData as any[]).map((t: any) => {
        const features = Array.isArray(t.features)
          ? t.features
          : (Array.isArray(t.benefits) ? t.benefits : []);
        const priceMonthly = typeof t.price === 'number'
          ? t.price
          : (typeof t.priceYear === 'number' ? Math.round(t.priceYear / 12) : 0);
        return {
          ...t,
          features,
          price: priceMonthly,
          icon: t.icon || '💎',
          color: t.color || '#2563eb',
          currentMembers: typeof t.currentMembers === 'number' ? t.currentMembers : 0,
        } as MembershipTier;
      });
      setMembershipTiers(normalizedTiers);

      // Load payment methods
      if (user) {
        const paymentQuery = query(collection(db, 'paymentMethods'), where('userId', '==', user.uid));
        const paymentSnapshot = await getDocs(paymentQuery);
        const paymentData = paymentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PaymentMethod[];
        setPaymentMethods(paymentData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading membership data:', error);
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier: MembershipTier) => {
    setSelectedTier(tier);
    setShowCheckout(true);
  };

  const startTrial = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) { alert('Login vereist'); return; }
      const trialEnds = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      await setDoc(docRef(db, 'entitlements', uid), {
        status: 'trial',
        trialEndsAt: trialEnds,
        plan: 'trial-7d',
        createdAt: new Date(),
        updatedAt: new Date()
      }, { merge: true });
      alert('✅ Je 7-daagse trial is gestart!');
    } catch (e) {
      console.error(e);
      alert('Kan trial niet starten.');
    }
  };

  const processPayment = async (
    tierOverride?: MembershipTier,
    preferredMethod?: 'ideal' | 'paypal' | 'creditcard'
  ) => {
    try {
      const tier = tierOverride || selectedTier;
      if (!tier) return;
      const amountValue = billingCycle === 'yearly'
        ? (tier.price * 12 * (1 - (tier.discount || 0) / 100)).toFixed(2)
        : tier.price.toFixed(2);
      const { checkoutUrl } = await createCheckoutSession({
        amount: { value: String(amountValue), currency: 'EUR' },
        description: `${tier.name} (${billingCycle})`,
        returnUrl: window.location.origin + '/#membership',
        tierId: tier.id
      });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error starting checkout:', error);
      alert('Kan checkout niet starten.');
    }
  };

  const simulatePayment = async (): Promise<boolean> => {
    // Simulate payment processing delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    // Simulate 95% success rate
    return Math.random() > 0.05;
  };

  const cancelSubscription = async () => {
    try {
      if (!userSubscription) return;

      const subscriptionRef = doc(db, 'membershipSubscriptions', userSubscription.id);
      await updateDoc(subscriptionRef, {
        status: 'cancelled',
        autoRenew: false,
        updatedAt: new Date()
      });

      loadData();
      alert('Je lidmaatschap is geannuleerd. Je hebt nog toegang tot je lidmaatschap tot het einde van je betaalperiode.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Er is een fout opgetreden bij het annuleren van je lidmaatschap.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header - Exact zoals PartnerConnect */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-lg p-6 mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">💎 SuperKonnected Lidmaatschap</h1>
        <p className="text-blue-100">
          Kies het perfecte lidmaatschap voor jouw ondernemersreis
        </p>
      </div>

      {/* Navigation Tabs - Met oranje gradient voor actieve tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('plans')}
          className={`flex-1 py-2 px-4 rounded-md font-medium  ${
            activeTab === 'plans' ? 'bg-gradient-to-b from-orange-400 to-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:text-gray-900'
          }`}
        >
          💎 Plannen
        </button>
        {userSubscription && (
          <>
            <button
              onClick={() => setActiveTab('current')}
              className={`flex-1 py-2 px-4 rounded-md font-medium  ${
                activeTab === 'current' ? 'bg-gradient-to-b from-orange-400 to-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:text-gray-900'
              }`}
            >
              📋 Huidig
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`flex-1 py-2 px-4 rounded-md font-medium  ${
                activeTab === 'billing' ? 'bg-gradient-to-b from-orange-400 to-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:text-gray-900'
              }`}
            >
              💳 Facturering
            </button>
          </>
        )}
        <button
          onClick={() => setActiveTab('benefits')}
          className={`flex-1 py-2 px-4 rounded-md font-medium  ${
            activeTab === 'benefits' ? 'bg-gradient-to-b from-orange-400 to-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:text-gray-900'
          }`}
        >
          🎁 Voordelen
        </button>
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            {user && <TrialBanner uid={user.uid} className="mb-4" />}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Kies je Lidmaatschap</h2>
            <p className="text-gray-600">Selecteer het plan dat het beste past bij jouw ondernemersdoelen</p>
            <div className="mt-4">
              {(entitlement.status !== 'trial' && entitlement.status !== 'active') && (
                <button onClick={startTrial} className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700">Start 7 dagen gratis</button>
              )}
            </div>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md font-medium  ${
                  billingCycle === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Maandelijks
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-md font-medium  ${
                  billingCycle === 'yearly' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Jaarlijks
                <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                  -20%
                </span>
              </button>
            </div>
          </div>

          {/* Membership Tiers */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {membershipTiers.sort((a, b) => (a.order || 0) - (b.order || 0)).map((tier) => (
              <div key={tier.id} className={`bg-white rounded-lg shadow-md p-6 border-2 ${
                tier.isPopular ? 'border-blue-500' : 'border-gray-200'
              }`}>
                {tier.isPopular && (
                  <div className="bg-blue-500 text-white text-center py-1 px-3 rounded-full text-sm font-medium mb-4">
                    Meest Populair
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <div className="text-4xl mb-2">{tier.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {tier.isCustomPricing ? (
                      <span className="text-lg text-gray-600">Maatwerk</span>
                    ) : (
                      <>
                        €{billingCycle === 'yearly' ? Math.round(tier.price * 12 * (1 - (tier.discount || 0) / 100)) : tier.price}
                        <span className="text-lg text-gray-600 font-normal">
                          /{billingCycle === 'yearly' ? 'jaar' : 'maand'}
                        </span>
                      </>
                    )}
                  </div>
                  {tier.tagline && (
                    <p className="text-sm font-medium text-gray-700 mb-2 italic">"{tier.tagline}"</p>
                  )}
                  <p className="text-gray-600 text-sm">{tier.description}</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-700">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Custom pricing or regular payment */}
                {tier.isCustomPricing ? (
                  <button
                    onClick={() => window.open('mailto:info@superkonnected.nl?subject=Connector%20Lidmaatschap%20Informatie', '_blank')}
                    className="w-full py-3 rounded-lg font-medium  bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Ga in gesprek
                  </button>
                ) : (
                  <>
                    {/* Primary: direct iDEAL */}
                    <button
                      onClick={() => processPayment(tier, 'ideal')}
                      className={`w-full py-3 rounded-lg font-medium  ${
                        tier.isPopular
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                    >
                      {userSubscription?.tierId === tier.id ? 'Huidig Plan' : 'Betaal met iDEAL'}
                    </button>

                    {/* Secondary: other methods */}
                    <div className="mt-3 text-center">
                      <button
                        onClick={() => processPayment(tier)}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        Andere betaalmethoden
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Subscription Tab */}
      {activeTab === 'current' && userSubscription && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Huidig Lidmaatschap</h2>
          
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{userSubscription.tierName}</h3>
                <p className="text-gray-600">€{userSubscription.price}/{userSubscription.billingCycle === 'yearly' ? 'jaar' : 'maand'}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(userSubscription.status)}`}>
                {userSubscription.status === 'active' ? 'Actief' : 'Geannuleerd'}
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Lidmaatschap Details</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>Gestart: {userSubscription.startDate.toLocaleDateString('nl-NL')}</div>
                  <div>Eindigt: {userSubscription.endDate.toLocaleDateString('nl-NL')}</div>
                  <div>Volgende factuur: {userSubscription.nextBillingDate.toLocaleDateString('nl-NL')}</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Facturering</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>Cyclus: {userSubscription.billingCycle === 'yearly' ? 'Jaarlijks' : 'Maandelijks'}</div>
                  <div>Auto-vernieuwing: {userSubscription.autoRenew ? 'Ja' : 'Nee'}</div>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium ">
                Beheer Abonnement
              </button>
              <button
                onClick={cancelSubscription}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium "
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Facturering & Betaling</h2>
          
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Betaalmethoden</h3>
              {paymentMethods.length > 0 ? (
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">
                          {method.brand} •••• {method.last4}
                        </div>
                        <div className="text-sm text-gray-600">Verloopt {method.expiryDate}</div>
                      </div>
                      {method.isDefault && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                          Standaard
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">Geen betaalmethoden gevonden.</p>
              )}
              <button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium ">
                + Betaalmethode Toevoegen
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Factuur Geschiedenis</h3>
              <p className="text-gray-600">Nog geen facturen beschikbaar.</p>
            </div>
          </div>
        </div>
      )}

      {/* Benefits Tab */}
      {activeTab === 'benefits' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Lidmaatschap Voordelen</h2>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="text-3xl mb-4">🎓</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Training Modules</h3>
              <p className="text-gray-600 text-sm">Toegang tot alle training modules en workshops</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="text-3xl mb-4">🤝</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Netwerken</h3>
              <p className="text-gray-600 text-sm">Connectie met andere ondernemers en experts</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="text-3xl mb-4">🤖</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Assistant</h3>
              <p className="text-gray-600 text-sm">Persoonlijke AI ondersteuning voor je business</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="text-3xl mb-4">📊</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics</h3>
              <p className="text-gray-600 text-sm">Gedetailleerde insights en rapportages</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="text-3xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Matching</h3>
              <p className="text-gray-600 text-sm">AI-powered matching met relevante connecties</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="text-3xl mb-4">💡</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Advies</h3>
              <p className="text-gray-600 text-sm">Directe toegang tot business experts</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipPage;
